// frontend/src/pages/Dashboard.jsx
// REPLICA EXACTA DE dashboard.html CON TEMAS REACTIVOS
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import ArbolFamilias from '../components/dashboard/ArbolFamilias';
import DetalleEquipo from '../components/dashboard/DetalleEquipo';
import Modal from '../components/common/Modal';
import DynamicFilterBuilder from '../components/common/DynamicFilterBuilder';
import ModalBusqueda from '../components/dashboard/ModalBusqueda';
import equiposService from '../services/equiposService';
import uiPreferencesService from '../services/uiPreferencesService';

const DASHBOARD_CLOUD_SCOPE = 'dashboard.arbol.filters.v2';
const EMPTY_FILTER_STATE = {
  columnFilters: {},
  touchedFilters: {},
  expandedFilterGroups: {},
};

// CONTENEDOR PRINCIPAL (REPLICA EL CONTENIDO DE dashboard.html)
const DashboardContainer = styled.div`
  flex: 1; /* Toma el espacio restante del layout */
  display: flex;
  min-height: 0; /* Permite que el contenido interno use todo el alto disponible */
  overflow: hidden; /* NO scroll en el contenedor principal */
  background: ${props => props.theme.colors.bgPrincipal};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

// CONTENEDOR REDIMENSIONABLE PARA LA FLOTA (IZQUIERDA)
const HydrationNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const FlotaContainer = styled.div`
  width: ${props => props.width}px;
  min-width: 250px;
  max-width: 600px;
  background: ${props => props.theme.colors.bgPrincipal};
  border-right: 1px solid ${props => props.theme.colors.borderDefault};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    border-right: none;
    display: ${({ $mobileShowDetalle }) => $mobileShowDetalle ? 'none' : 'flex'};
  }
`;

// BARRA DE REDIMENSIONAMIENTO
const ResizeBar = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: ${props => props.theme.colors.borderDefault};
  cursor: col-resize;
  transition-property: background-color, width;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    inset: 0 -6px;
  }

  &:hover {
    background: ${props => props.theme.colors.primary};
    width: 6px;
  }

  &:active {
    background: ${props => props.theme.colors.primaryHover};
    width: 6px;
  }
`;

// CONTENEDOR DEL DETALLE (DERECHA)
const DetalleContainer = styled.div`
  flex: 1;
  background: ${props => props.theme.colors.bgPrincipal};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: ${({ $mobileShowDetalle }) => $mobileShowDetalle ? 'flex' : 'none'};
    width: 100%;
  }
`;

// BOTÓN VOLVER AL ÁRBOL (SÓLO MÓVIL)
const MobileBackButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: ${({ theme }) => theme.colors.bgSecundario};
    border: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
    color: ${({ theme }) => theme.colors.primary};
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    width: 100%;
    flex-shrink: 0;
  }
`;

const Dashboard = () => {
  // HOOK PARA LEER PARÁMETROS DE URL (equipoId desde las listas)
  const [searchParams, setSearchParams] = useSearchParams();
  const filtrosHydratedRef = useRef(false);
  const restoredSelectedEquipoIdRef = useRef(null);

  // ESTADOS PARA MANEJAR LOS MODALES Y SELECCIÓN
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [initialTab, setInitialTab] = useState(null);
  const [showModalFiltros, setShowModalFiltros] = useState(false);
  const [showModalBusqueda, setShowModalBusqueda] = useState(false);
  const [dynamicFilters, setDynamicFilters] = useState(EMPTY_FILTER_STATE);
  
  // ESTADOS PARA DATOS DEL BACKEND
  const [familias, setFamilias] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [equiposFiltrados, setEquiposFiltrados] = useState([]); // EQUIPOS DESPUÉS DE APLICAR FILTROS
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // false mientras solo está el primer tramo rápido (el árbol/búsqueda aún no
  // cubren toda la flota); true cuando llegó el dataset completo
  const [equiposCompletos, setEquiposCompletos] = useState(false);
  
  // ESTADO PARA EL ANCHO DE LA FLOTA (REDIMENSIONABLE)
  const [flotaWidth, setFlotaWidth] = useState(350);
  const [isResizing, setIsResizing] = useState(false);

  // MÓVIL: controla si se muestra el árbol o el detalle (una vista a la vez)
  const [mobileShowDetalle, setMobileShowDetalle] = useState(false);

  const dashboardFilterColumns = useMemo(() => ([
    { key: 'familia', label: 'Familia', getValue: (equipo) => String(equipo?.familia_nombre || equipo?.familia || '-').trim() },
    { key: 'sucursal', label: 'Sucursal', getValue: (equipo) => String(equipo?.sucursal_nombre || equipo?.sucursal || '-').trim() },
    { key: 'estado', label: 'Estado', getValue: (equipo) => String(equipo?.estado_nombre || equipo?.estado || '-').trim() },
    { key: 'tipo', label: 'Tipo', getValue: (equipo) => String(equipo?.tipo_nombre || equipo?.tipo || '-').trim() },
    { key: 'empresa', label: 'Empresa', getValue: (equipo) => String(equipo?.empresa_nombre || equipo?.empresa || '-').trim() },
    { key: 'obra', label: 'Obra', getValue: (equipo) => String(equipo?.obra_nombre || equipo?.obra || '-').trim() },
    { key: 'ubicacion', label: 'Ubicacion', getValue: (equipo) => String(equipo?.ubicacion_nombre || equipo?.ubicacion || '-').trim() },
  ]), []);

  // HANDLERS PARA LOS MODALES
  const handleOpenFiltros = () => setShowModalFiltros(true);
  const handleCloseFiltros = () => setShowModalFiltros(false);
  const handleOpenBusqueda = () => setShowModalBusqueda(true);
  const handleCloseBusqueda = () => setShowModalBusqueda(false);

  // FUNCIÓN PARA APLICAR FILTROS (reutilizable)
  const applyFiltersToEquipos = useCallback((equiposArray, filtersToApply) => {
    const touched = filtersToApply?.touchedFilters || {};
    const columnFilters = filtersToApply?.columnFilters || {};

    return (equiposArray || []).filter((equipo) => {
      for (const column of dashboardFilterColumns) {
        if (!touched[column.key]) continue;

        const selected = Array.isArray(columnFilters[column.key]) ? columnFilters[column.key] : [];
        const value = column.getValue ? column.getValue(equipo) : String(equipo?.[column.key] || '').trim();

        if (!selected.length) return false;
        if (!selected.includes(value)) return false;
      }

      return true;
    });
  }, [dashboardFilterColumns]);

  const applyStoredPreferences = useCallback((parsed) => {
    if (!parsed || typeof parsed !== 'object') return;
    if (parsed.dynamicFilters && typeof parsed.dynamicFilters === 'object') {
      setDynamicFilters({
        columnFilters: parsed.dynamicFilters.columnFilters || {},
        touchedFilters: parsed.dynamicFilters.touchedFilters || {},
        expandedFilterGroups: parsed.dynamicFilters.expandedFilterGroups || {},
      });
    }

    if (parsed.selectedEquipoId != null && parsed.selectedEquipoId !== '') {
      restoredSelectedEquipoIdRef.current = String(parsed.selectedEquipoId);
    }
  }, []);

  useEffect(() => {
    let active = true;
    filtrosHydratedRef.current = false;
    restoredSelectedEquipoIdRef.current = null;

    const hydratePreferences = async () => {
      try {
        const cloudValue = await uiPreferencesService.getScope(DASHBOARD_CLOUD_SCOPE);
        if (!active || !cloudValue) return;
        applyStoredPreferences(cloudValue);
      } catch (error) {
        console.warn('No se pudieron restaurar filtros cloud de dashboard', error);
      } finally {
        if (active) filtrosHydratedRef.current = true;
      }
    };

    hydratePreferences();

    return () => {
      active = false;
    };
  }, [applyStoredPreferences]);

  useEffect(() => {
    if (!filtrosHydratedRef.current) return;
    // No pisar el ID persistido mientras haya una restauración pendiente:
    // al re-montar (p. ej. al volver desde Informes) selectedEquipo arranca en
    // null y el efecto de restauración aún no corrió porque espera a que carguen
    // los equipos. Guardar aquí escribiría selectedEquipoId: null y borraría la
    // selección antes de poder restaurarla.
    if (restoredSelectedEquipoIdRef.current && !selectedEquipo) return;

    const payload = {
      dynamicFilters,
      selectedEquipoId: selectedEquipo?.id ?? null,
    };

    const timeoutId = setTimeout(() => {
      uiPreferencesService.setScope(DASHBOARD_CLOUD_SCOPE, payload).catch((error) => {
        console.warn('No se pudieron guardar filtros cloud de dashboard', error);
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [dynamicFilters, selectedEquipo]);

  const handleSelectEquipo = (equipo) => {
    setSelectedEquipo(equipo);
    setMobileShowDetalle(true);

    // Si viene desde la búsqueda, cerrar el modal
    if (showModalBusqueda) {
      setShowModalBusqueda(false);
    }
  };

  // HANDLERS PARA EL REDIMENSIONAMIENTO
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    if (newWidth >= 250 && newWidth <= 600) {
      setFlotaWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // EFECTOS PARA EL REDIMENSIONAMIENTO
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // CARGAR DATOS INICIALES DEL BACKEND (API REAL)
  useEffect(() => {
    let activo = true;
    // true cuando ya llegó el dataset COMPLETO (el primer tramo rápido nunca
    // debe pisar a la carga completa si esta resolvió antes)
    let completoListo = false;

    const cargarDatosIniciales = async () => {
      setLoading(true);
      setError(null);

      try {
        // Primer tramo rápido: pinta el árbol al instante mientras la carga
        // completa sigue en segundo plano. Solo si no hay cache completa ya.
        if (!equiposService.tieneCacheEquipos()) {
          equiposService.getPrimerTramoEquipos(150).then((tramo) => {
            if (!activo || completoListo || !tramo?.data?.length) return;
            setEquipos(tramo.data);
            setLoading(false);
          }).catch(() => { /* la carga completa sigue siendo la fuente de verdad */ });
        }

        // Cargar familias y equipos COMPLETOS en paralelo desde la API real
        const [familiasResult, equiposResult] = await Promise.all([
          equiposService.getFamilias(),
          equiposService.getTodosEquipos()
        ]);
        if (!activo) return;
        completoListo = true;

        // Los servicios devuelven { data: [...] } directamente
        if (familiasResult && familiasResult.data) {
          setFamilias(familiasResult.data);
        } else {
          console.error('❌ Error: familiasResult no tiene data');
          setError('Error cargando familias: no hay datos');
        }

        if (equiposResult && equiposResult.data) {
          setEquipos(equiposResult.data);
          setEquiposCompletos(true);
        } else {
          console.error('❌ Error: equiposResult no tiene data');
          setError('Error cargando equipos: no hay datos');
        }

      } catch (error) {
        if (!activo) return;
        console.error('💥 Error cargando datos iniciales:', error);
        setError('Error al cargar los datos del servidor: ' + error.message);
      } finally {
        if (activo) setLoading(false);
      }
    };

    cargarDatosIniciales();
    return () => { activo = false; };
  // Traer los datos NO depende de los filtros: el efecto de abajo re-aplica el
  // filtrado cada vez que cambian. Con `dynamicFilters` acá, la llegada de las
  // preferencias guardadas relanzaba la carga entera del árbol y del catálogo
  // de familias — el mismo patrón de doble pedido que tenían los listados.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEquiposFiltrados(applyFiltersToEquipos(equipos, dynamicFilters));
  }, [equipos, dynamicFilters, applyFiltersToEquipos]);

  // EFECTO PARA MANEJAR SELECCIÓN DE EQUIPO DESDE URL (equipoId + tab)
  const equipoIdParam = searchParams.get('equipoId');
  const tabParam = searchParams.get('tab');

  useEffect(() => {
    if (equipoIdParam && equipos.length > 0) {
      const equipoEncontrado = equipos.find(equipo => equipo.id.toString() === equipoIdParam);
      if (equipoEncontrado) {
        setSelectedEquipo(equipoEncontrado);
        if (tabParam) setInitialTab(tabParam);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('equipoId');
        newSearchParams.delete('tab');
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [equipoIdParam, tabParam, equipos, searchParams, setSearchParams]);

  // Restaurar selección persistida cuando se vuelve al módulo Dashboard.
  // Si hay un equipoIdParam en la URL, ese tiene prioridad y este efecto no debe pisar.
  useEffect(() => {
    if (selectedEquipo || equipos.length === 0) return;
    if (equipoIdParam) return;
    const storedId = restoredSelectedEquipoIdRef.current;
    if (!storedId) return;
    const equipoPersistido = equipos.find((equipo) => String(equipo.id) === String(storedId));
    // Consumir la restauración pendiente: si el equipo ya no existe en la flota
    // (filtrado/eliminado), limpiar la ref igual para no bloquear el guardado.
    restoredSelectedEquipoIdRef.current = null;
    if (equipoPersistido) {
      setSelectedEquipo(equipoPersistido);
    }
  }, [equipos, selectedEquipo, equipoIdParam]);

  return (
    <DashboardContainer>
      {/* PANEL IZQUIERDO - FLOTA DE EQUIPOS REDIMENSIONABLE */}
      <FlotaContainer width={flotaWidth} $mobileShowDetalle={mobileShowDetalle}>
        {!loading && !equiposCompletos && !error && (
          <HydrationNote>
            <i className="bi bi-arrow-repeat" />
            Cargando la flota completa en segundo plano…
          </HydrationNote>
        )}
        <ArbolFamilias
          onSelectEquipo={handleSelectEquipo}
          onOpenFiltros={handleOpenFiltros}
          onOpenBusqueda={handleOpenBusqueda}
          familias={familias}
          equipos={equiposFiltrados}
          loading={loading}
          error={error}
          selectedEquipoId={selectedEquipo?.id}
        />

        {/* BARRA DE REDIMENSIONAMIENTO */}
        <ResizeBar onMouseDown={handleMouseDown} />
      </FlotaContainer>

      {/* PANEL DERECHO - DETALLE DEL EQUIPO */}
      <DetalleContainer $mobileShowDetalle={mobileShowDetalle}>
        {/* BOTÓN VOLVER AL ÁRBOL (SÓLO VISIBLE EN MÓVIL) */}
        <MobileBackButton onClick={() => setMobileShowDetalle(false)}>
          <i className="bi bi-arrow-left"></i>
          Flota de equipos
        </MobileBackButton>
        <DetalleEquipo
          equipo={selectedEquipo}
          initialTab={initialTab}
          onTabApplied={() => setInitialTab(null)}
        />
      </DetalleContainer>

      {/* MODALES */}
      {showModalFiltros && (
        <Modal isOpen={showModalFiltros} onClose={handleCloseFiltros} title="Filtros de equipos" size="md">
          <DynamicFilterBuilder
            title="Filtros del arbol"
            rows={equipos}
            columns={dashboardFilterColumns}
            value={dynamicFilters}
            onChange={setDynamicFilters}
          />
        </Modal>
      )}

      {showModalBusqueda && (
        <ModalBusqueda
          show={showModalBusqueda}
          onClose={handleCloseBusqueda}
          onSelectEquipo={handleSelectEquipo}
          equiposFiltrados={equiposFiltrados} // PASAR SOLO EQUIPOS FILTRADOS
        />
      )}
    </DashboardContainer>
  );
};

export default Dashboard;
